import { Close, Visibility } from '@mui/icons-material';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import { Box, Button, Fade, FormLabel, Grid, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';
import { Modal } from '../modal';
import { imageFileTypes } from './utils';
import { useSnackbar } from 'notistack';

interface IMultiImageUpload {
  handleFileChange: (val: any[], file?: File[]) => void;
  images: string[];
  disabled?: boolean;
  isRequired?: boolean;
  showSource?: boolean;
  imageKey: string;
}

export const MultiImageModal = ({ isOpen, onClose, sources }) => {
  const classes = useStyles();
  return (
    <Modal
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <Fade in={isOpen}>
        <Grid container className={classes.root}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" className={classes.imgBox}>
              {sources &&
                sources.length > 0 &&
                sources.map(source => (
                  <img
                    key={source.name}
                    src={`data:image/jpeg;base64,${source.contents}`}
                    alt={'snap'}
                    className={classes.img}
                  />
                ))}
            </Box>
          </Grid>
        </Grid>
      </Fade>
    </Modal>
  );
};

export const MultiImageUpload: FC<IMultiImageUpload> = ({
  handleFileChange,
  images,
  disabled = false,
  isRequired = false,
  showSource = true,
  imageKey
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [isShowingImage, showImage] = useState<boolean>(false);

  const createBase64Image = async file => {
    const reader = new FileReader();
    return new Promise(function (resolve, reject) {
      reader.onload = function (event) {
        resolve(event.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCapture = async target => {
    if (target.files) {
      if (target.files.length !== 0) {
        const files = [];

        const base64Images: Array<{ name: string; contents: string }> = [];
        // Get fileList Object, convert to an array and get the values before they disappear
        // https://stackoverflow.com/questions/25333488/why-isnt-the-filelist-object-an-array
        const allFiles: File[] = Array.from(target.files);
        const fileLength = target.files.length;
        for (let i = 0; i < fileLength; i++) {
          const file = allFiles[i];
          // file.type is in the format of 'image/png' or 'application/pdf' or 'text/csv'
          const uploadedFileType = file.type.split?.('/')?.[1];

          if (!imageFileTypes.includes(`.${uploadedFileType}`)) {
            return enqueueSnackbar(
              `Invalid file type, ${uploadedFileType}. Please upload one of these image types, ${imageFileTypes.join(
                ', '
              )}.`,
              {
                variant: 'error'
              }
            );
          }
          const base64 = (await createBase64Image(file)) as string;
          const newUrl = URL.createObjectURL(file);
          files.push(file);
          base64Images.push({ name: newUrl, contents: base64.split(',')[1] });
        }
        handleFileChange(base64Images, files);
      }
    }
  };

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <FormLabel required={isRequired} className={classes.heading}>
          Upload Images
        </FormLabel>
        {isRequired && images.length > 0 && (
          <Typography variant="caption" className={classes.imageHelpText}>
            Uploading an image is required.
          </Typography>
        )}

        {images.length > 0 && showSource && (
          <>
            <Box display="flex">
              <Button
                startIcon={<Visibility />}
                color="primary"
                onClick={() => {
                  showImage(true);
                }}
              >
                View Images
              </Button>
              <Button
                onClick={() => {
                  const result = window.confirm('Are you sure you want to remove this image?');
                  if (result) {
                    handleFileChange([]);
                  }
                }}
                className={classes.removeButton}
                startIcon={<Close />}
              >
                Remove Images
              </Button>
            </Box>
            <Box marginTop="8px">
              <Typography component="p" variant="caption">
                Click the file name to view the whole image.
              </Typography>
            </Box>
          </>
        )}
        <input
          accept="image/*"
          disabled={disabled}
          className={classes.input}
          id={`icon-button-file-${imageKey || ''}`}
          type="file"
          capture="environment"
          multiple
          onChange={e => {
            handleCapture(e.target);
            // reset the input value so you can re-upload the same photo if you want to
            e.target.value = '';
          }}
        />

        {(images.length < 1 || !showSource) && (
          <Box marginLeft="1rem">
            <label htmlFor={`icon-button-file-${imageKey || ''}`}>
              <IconButton
                color="primary"
                disabled={disabled}
                aria-label="upload picture"
                component="span"
                size="large"
              >
                <PhotoCameraRoundedIcon
                  fontSize="large"
                  color={disabled ? 'disabled' : 'primary'}
                />
              </IconButton>
            </label>
          </Box>
        )}
      </Grid>
      <MultiImageModal isOpen={isShowingImage} sources={images} onClose={() => showImage(false)} />
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  root: {},
  heading: {
    marginTop: theme.spacing(1),
    display: 'block'
  },
  imgBox: {
    maxWidth: '100%',
    flexDirection: 'column'
  },
  img: {
    height: 'inherit',
    maxWidth: 'inherit',
    padding: theme.spacing(1)
  },
  input: {
    display: 'none'
  },
  removeButton: {
    color: theme.palette.error.main
  },
  imageHelpText: {
    color: theme.palette.error.main
  }
}));
